import React, { useState, useRef, useEffect } from 'react'
import SignaturePad from 'react-signature-canvas'
import Dialog from '@material-ui/core/Dialog'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import { Input } from '@material-ui/core'
import { connect } from 'react-redux'
import { css } from 'styled-components'
import Button from 'components/common/Button/Button'
import { saveSignature } from 'reducers/profile/reducer'
import { injectIntl } from 'react-intl'
import { DB_AZURE } from 'constants/colors'
import withPromise from 'helpers/withPromise'
import Loader from 'components/Loading/SmallLoader'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import {
  ModalHeader,
  ModalTitle,
  ModalFooter,
  ModalContent,
  TextFieldContainer,
  SignatureContainer,
  SavedSignatureContainer,
} from './SignatureModal.styles'
import { BUTTON_TYPE_PRIMARY, BUTTON_TYPE_SECONDARY } from '../Button/constants'
import { SignatureModalProps } from './SignatureModal.types'
import messages from './translate'

export const SignatureModalComponent = ({
  isOpen,
  toggleIsOpen,
  showNameField = false,
  saveSignature,
  title,
  confirmButtonText,
  intl: { formatMessage },
  isSignatureSaving,
  signatureTemplate = null,
  setUpdatedSignature,
}: SignatureModalProps) => {
  const [savedSignature, setSavedSignature] = useState<string | null>(
    signatureTemplate,
  )
  const [name, setName] = useState('')
  const ref = useRef<SignaturePad>(null)
  const [signature, setSignature] = useState<string | null>(null)

  useEffect(() => {
    if (signatureTemplate && isOpen) {
      setSavedSignature(signatureTemplate)
    }
  }, [isOpen])

  const handleSignatureEnd = () => {
    setSignature(ref.current?.getTrimmedCanvas().toDataURL() || '')
  }

  const clearSignature = () => {
    ref.current?.clear()
    setSignature(null)
  }

  const isButtonDisabled =
    !savedSignature &&
    (!ref.current?.toDataURL().length ||
      ref.current?.isEmpty() ||
      isSignatureSaving ||
      (showNameField && !name.length))

  const renderModalContent = () => (
    <>
      <ModalHeader align="center" justify="flex-start">
        <ModalTitle>{title}</ModalTitle>
        <IconButton
          size="small"
          onClick={toggleIsOpen}
          aria-label="close modal">
          <CloseIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent>
        <SignatureContainer
          isMobileView={ifWindowWidthLessThan(TABLET_BREAKPOINT)}>
          {savedSignature ? (
            <SavedSignatureContainer
              isMobileView={ifWindowWidthLessThan(TABLET_BREAKPOINT)}
              align="center"
              justify="center">
              <img src={savedSignature} alt="" />
            </SavedSignatureContainer>
          ) : (
            <SignaturePad ref={ref} onEnd={handleSignatureEnd} />
          )}
        </SignatureContainer>
        {showNameField && (
          <TextFieldContainer>
            <Input
              disableUnderline
              fullWidth
              placeholder={formatMessage(messages.enterNamePlaceholder)}
              value={name}
              onClick={event => event.stopPropagation()}
              onChange={({ target: { value } }) => setName(value)}
            />
          </TextFieldContainer>
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          disabled={isButtonDisabled}
          onClick={() => {
            if (savedSignature) {
              toggleIsOpen()

              return
            }

            if (setUpdatedSignature) {
              setUpdatedSignature(signature, name)
            }

            if (!showNameField) {
              withPromise(saveSignature, {
                signature,
                successMessage: formatMessage(messages.saveSuccessMessage),
              }).then(() => {
                toggleIsOpen()
              })
            }

            if (showNameField) {
              setName('')
              toggleIsOpen()
            }
          }}
          customStyles={css`
            height: 42px;
            width: 80px;
            background: ${DB_AZURE};
          `}
          type={BUTTON_TYPE_PRIMARY}>
          {isSignatureSaving ? <Loader /> : confirmButtonText}
        </Button>
        <Button
          onClick={() => {
            if (savedSignature) {
              setSavedSignature(null)

              return
            }
            clearSignature()
          }}
          disabled={isButtonDisabled}
          customStyles={css`
            height: 42px;
            width: 80px;
            margin-left: 8px;
          `}
          type={BUTTON_TYPE_SECONDARY}>
          {formatMessage(messages.clear)}
        </Button>
      </ModalFooter>
    </>
  )

  return ifWindowWidthLessThan(TABLET_BREAKPOINT) ? (
    renderModalContent()
  ) : (
    <Dialog open={isOpen} onClose={toggleIsOpen}>
      {renderModalContent()}
    </Dialog>
  )
}

export default connect(null, {
  saveSignature,
})(injectIntl(SignatureModalComponent))
